import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";

type TEmailBuilderDiscardDraftModalProps = {
  open: boolean;
  discarding: boolean;
  description: string;
  onClose: () => void;
  onConfirm: () => void;
};

export function EmailBuilderDiscardDraftModal(props: TEmailBuilderDiscardDraftModalProps) {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isMobile = (windowSize.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || props.discarding) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, props.discarding]);

  function handleClose() {
    if (props.discarding) {
      return;
    }

    props.onClose();
  }

  if (isMobile) {
    return (
      <Sheet
        open={props.open}
        size="small"
        title={t("Discard draft")}
        supportText={props.description}
        closable
        onCancel={handleClose}
        cancelText={t("Cancel")}
        okText={t("Discard")}
        onOk={props.onConfirm}
        okLoading={props.discarding}
        okDisabled={props.discarding}
        cancelDisabled={props.discarding}
        backDropCancel={!props.discarding}
      />
    );
  }

  return (
    <Popup
      open={props.open}
      type="warning"
      title={t("Discard draft")}
      description={props.description}
      closable
      onCancel={handleClose}
      cancelText={t("Cancel")}
      okText={props.discarding ? t("Discarding…") : t("Discard")}
      onOk={props.onConfirm}
      okDisabled={props.discarding}
      cancelDisabled={props.discarding}
      backDropCancel={!props.discarding}
    />
  );
}
